import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { X, ChevronLeft, ChevronRight, ChevronDown, Camera, Search, Images, MapPin, Calendar, Play, SlidersHorizontal } from 'lucide-react';
import { dzialajLokalnieGallery } from '@/data/dzialajLokalnieGallery';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';

type MediaType = 'image' | 'video';
type VideoProvider = 'youtube' | 'vimeo' | 'file';

interface DbRow {
  id: string;
  image_url: string;
  image_avif_url?: string | null;
  image_webp_url?: string | null;
  caption?: string | null;
  title?: string | null;
  description?: string | null;
  municipality?: string | null;
  year?: string | null;
  credit?: string | null;
  media_type?: MediaType | null;
  video_url?: string | null;
  video_provider?: VideoProvider | null;
  focal_x?: number | string | null;
  focal_y?: number | string | null;
  sort_order?: number | null;
}

interface GalleryItem {
  id: string;
  image_url: string;
  avif?: string;
  webp?: string;
  title: string;
  description?: string;
  municipality?: string;
  year?: string;
  credit?: string;
  media_type: MediaType;
  video_url?: string;
  video_provider?: VideoProvider;
  focal_x: number | null;
  focal_y: number | null;
  source: 'dl' | 'db';
}

const PAGE_SIZE = 8;

const focalStyle = (fx: number | null, fy: number | null) => ({
  objectPosition: fx != null && fy != null ? `${fx}% ${fy}%` : '50% 0%',
});

function youtubeId(url: string): string | null {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? m[1] : null;
}
function vimeoId(url: string): string | null {
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
}

function VideoEmbed({ item }: { item: GalleryItem }) {
  if (!item.video_url) return null;
  if (item.video_provider === 'youtube') {
    const id = youtubeId(item.video_url);
    if (!id) return null;
    return (
      <div className="w-full aspect-video bg-black">
        <iframe
          className="w-full h-full"
          src={`https://www.youtube.com/embed/${id}?autoplay=1&rel=0`}
          title={item.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  if (item.video_provider === 'vimeo') {
    const id = vimeoId(item.video_url);
    if (!id) return null;
    return (
      <div className="w-full aspect-video bg-black">
        <iframe
          className="w-full h-full"
          src={`https://player.vimeo.com/video/${id}?autoplay=1`}
          title={item.title}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  return (
    <video className="w-full max-h-[60vh] bg-black" src={item.video_url} controls autoPlay playsInline />
  );
}

const GallerySection = () => {
  const { data: dbItems = [] } = useQuery<DbRow[]>({
    queryKey: ['gallery'],
    queryFn: async () => {
      const { data } = await supabase
        .from('gallery')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });
      return (data as DbRow[]) || [];
    },
  });

  const items = useMemo<GalleryItem[]>(() => {
    const dbMapped: GalleryItem[] = dbItems.map((row) => ({
      id: `db-${row.id}`,
      image_url: row.image_url,
      avif: row.image_avif_url ?? undefined,
      webp: row.image_webp_url ?? undefined,
      title: row.title || row.caption || 'Zdjęcie z galerii',
      description: row.description ?? undefined,
      municipality: row.municipality ?? undefined,
      year: row.year ?? undefined,
      credit: row.credit ?? undefined,
      media_type: (row.media_type ?? 'image') as MediaType,
      video_url: row.video_url ?? undefined,
      video_provider: (row.video_provider ?? undefined) as VideoProvider | undefined,
      focal_x: row.focal_x != null ? Number(row.focal_x) : null,
      focal_y: row.focal_y != null ? Number(row.focal_y) : null,
      source: 'db',
    }));
    const dlItems: GalleryItem[] = dzialajLokalnieGallery.map((it) => ({
      id: `dl-${it.id}`,
      image_url: it.image,
      avif: it.avif,
      webp: it.webp,
      title: it.title,
      description: it.description,
      municipality: it.municipality,
      year: it.year,
      credit: it.credit,
      media_type: 'image',
      focal_x: null,
      focal_y: null,
      source: 'dl',
    }));
    return [...dbMapped, ...dlItems];
  }, [dbItems]);

  // Filtry
  const [search, setSearch] = useState('');
  const [municipality, setMunicipality] = useState<string>('all');
  const [year, setYear] = useState<string>('all');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const municipalities = useMemo(
    () => Array.from(new Set(items.map((i) => i.municipality).filter(Boolean))) as string[],
    [items],
  );
  const years = useMemo(
    () =>
      Array.from(new Set(items.map((i) => i.year).filter(Boolean)))
        .sort((a, b) => (b as string).localeCompare(a as string)) as string[],
    [items],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      if (municipality !== 'all' && i.municipality !== municipality) return false;
      if (year !== 'all' && i.year !== year) return false;
      if (q) {
        const hay = `${i.title} ${i.description ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, search, municipality, year]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [search, municipality, year]);

  const visible = filtered.slice(0, visibleCount);
  const hasFilters = search !== '' || municipality !== 'all' || year !== 'all';
  const clearFilters = () => {
    setSearch('');
    setMunicipality('all');
    setYear('all');
  };

  const stats = useMemo(() => {
    const m = new Set(items.map((i) => i.municipality).filter(Boolean));
    const y = new Set(items.map((i) => i.year).filter(Boolean));
    return { total: items.length, municipalities: m.size, years: y.size };
  }, [items]);

  // Lightbox
  const [lightbox, setLightbox] = useState<number | null>(null);
  const triggerRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const prevBtnRef = useRef<HTMLButtonElement>(null);
  const nextBtnRef = useRef<HTMLButtonElement>(null);
  const lastTriggerIndex = useRef<number | null>(null);

  const openLightbox = (i: number) => {
    lastTriggerIndex.current = i;
    setLightbox(i);
  };

  const closeLightbox = useCallback(() => {
    setLightbox(null);
    setTimeout(() => {
      if (lastTriggerIndex.current !== null) {
        triggerRefs.current[lastTriggerIndex.current]?.focus();
      }
    }, 0);
  }, []);

  const navigate = useCallback(
    (dir: number) => {
      setLightbox((prev) => {
        if (prev === null) return prev;
        return (prev + dir + filtered.length) % filtered.length;
      });
    },
    [filtered.length],
  );

  useEffect(() => {
    if (lightbox !== null) setTimeout(() => closeBtnRef.current?.focus(), 0);
  }, [lightbox]);

  useEffect(() => {
    if (lightbox === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); closeLightbox(); }
      else if (e.key === 'ArrowLeft') navigate(-1);
      else if (e.key === 'ArrowRight') navigate(1);
      else if (e.key === 'Tab') {
        const focusables = [closeBtnRef.current, prevBtnRef.current, nextBtnRef.current].filter(Boolean) as HTMLButtonElement[];
        if (!focusables.length) return;
        const idx = focusables.indexOf(document.activeElement as HTMLButtonElement);
        e.preventDefault();
        const next = e.shiftKey
          ? focusables[(idx - 1 + focusables.length) % focusables.length]
          : focusables[(idx + 1) % focusables.length];
        next.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox, navigate, closeLightbox]);

  const current = lightbox !== null ? filtered[lightbox] : null;

  return (
    <section id="galeria" className="py-16 lg:py-24 bg-background">
      <div className="container">
        <p className="text-sm font-semibold text-secondary uppercase tracking-wider mb-2">
          Nasze działania w obrazach
        </p>
        <h2 className="text-3xl lg:text-4xl font-heading font-bold mb-4">Galeria</h2>
        <p className="text-muted-foreground max-w-2xl mb-6">
          Zdjęcia i filmy z naszych wydarzeń, projektów i inicjatyw — w tym z programu Działaj Lokalnie.
        </p>

        {items.length === 0 ? (
          <p className="text-muted-foreground py-6">Zdjęcia pojawią się wkrótce.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
              <div className="rounded-panel border border-border bg-card p-4 flex items-center gap-3">
                <Images className="w-5 h-5 text-primary" aria-hidden="true" />
                <div>
                  <div className="text-2xl font-heading font-bold leading-none">{stats.total}</div>
                  <div className="text-xs text-muted-foreground mt-1">pozycji</div>
                </div>
              </div>
              <div className="rounded-panel border border-border bg-card p-4 flex items-center gap-3">
                <MapPin className="w-5 h-5 text-primary" aria-hidden="true" />
                <div>
                  <div className="text-2xl font-heading font-bold leading-none">{stats.municipalities}</div>
                  <div className="text-xs text-muted-foreground mt-1">miejscowości</div>
                </div>
              </div>
              <div className="rounded-panel border border-border bg-card p-4 flex items-center gap-3">
                <Calendar className="w-5 h-5 text-primary" aria-hidden="true" />
                <div>
                  <div className="text-2xl font-heading font-bold leading-none">{stats.years}</div>
                  <div className="text-xs text-muted-foreground mt-1">lat aktywności</div>
                </div>
              </div>
              {items[0] && (
                <button
                  type="button"
                  onClick={() => {
                    const idx = filtered.findIndex((f) => f.id === items[0].id);
                    if (idx >= 0) openLightbox(idx);
                  }}
                  className="col-span-2 lg:col-span-1 group rounded-panel border border-border bg-card p-2 flex items-center gap-3 text-left hover:border-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  aria-label={`Ostatnio dodane: ${items[0].title}`}
                >
                  <picture className="shrink-0">
                    {items[0].avif && <source srcSet={items[0].avif} type="image/avif" />}
                    {items[0].webp && <source srcSet={items[0].webp} type="image/webp" />}
                    <img
                      src={items[0].image_url}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className="w-14 h-14 rounded-md object-cover"
                      style={focalStyle(items[0].focal_x, items[0].focal_y)}
                    />
                  </picture>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      Ostatnio dodane
                    </div>
                    <div className="text-sm sm:text-base font-heading font-bold leading-tight line-clamp-2 break-words group-hover:text-primary">
                      {items[0].title}
                    </div>
                  </div>
                </button>
              )}
            </div>

            <div className="flex flex-col gap-4 mb-6">
              <button
                type="button"
                id="gallery-filters-toggle"
                onClick={() => setFiltersOpen((v) => !v)}
                aria-expanded={filtersOpen}
                aria-controls="gallery-filters-panel"
                className={`self-start inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${hasFilters ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-foreground border-border hover:bg-muted'}`}
              >
                <SlidersHorizontal className="w-4 h-4" aria-hidden="true" />
                {filtersOpen ? 'Ukryj filtry' : 'Filtry'}
                {hasFilters && !filtersOpen && (
                  <span className="w-2 h-2 rounded-full bg-current ml-1" aria-hidden="true" />
                )}
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${filtersOpen ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                />
              </button>

              {filtersOpen && (
                <div id="gallery-filters-panel" className="flex flex-col gap-4">
                  <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
                    <Input
                      type="search"
                      placeholder="Szukaj po tytule lub opisie…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-9"
                      aria-label="Szukaj zdjęć"
                    />
                  </div>

                  {municipalities.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs uppercase tracking-wider text-muted-foreground mr-1">Miejscowość:</span>
                      <button
                        type="button"
                        onClick={() => setMunicipality('all')}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${municipality === 'all' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-foreground border-border hover:bg-muted'}`}
                      >
                        Wszystkie
                      </button>
                      {municipalities.map((m) => (
                        <button
                          key={m}
                          type="button"
                          onClick={() => setMunicipality(m)}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${municipality === m ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-foreground border-border hover:bg-muted'}`}
                        >
                          {m}
                        </button>
                      ))}
                    </div>
                  )}

                  {years.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs uppercase tracking-wider text-muted-foreground mr-1">Rok:</span>
                      <button
                        type="button"
                        onClick={() => setYear('all')}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${year === 'all' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-foreground border-border hover:bg-muted'}`}
                      >
                        Wszystkie
                      </button>
                      {years.map((y) => (
                        <button
                          key={y}
                          type="button"
                          onClick={() => setYear(y)}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${year === y ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-foreground border-border hover:bg-muted'}`}
                        >
                          {y}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <p className="text-sm text-muted-foreground">
                      Pokazano <span className="font-semibold text-foreground">{visible.length}</span> z{' '}
                      <span className="font-semibold text-foreground">{filtered.length}</span> pozycji
                    </p>
                    {hasFilters && (
                      <Button type="button" variant="ghost" size="sm" onClick={clearFilters}>
                        Wyczyść filtry
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-12 border border-dashed border-border rounded-panel">
                <p className="text-muted-foreground mb-4">Brak zdjęć dla wybranych filtrów.</p>
                <Button type="button" variant="outline" onClick={clearFilters}>
                  Wyczyść filtry
                </Button>
              </div>
            ) : (
              <>
                <div id="gallery-grid" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {visible.map((item, i) => (
                    <button
                      key={item.id}
                      ref={(el) => { triggerRefs.current[i] = el; }}
                      onClick={() => openLightbox(i)}
                      aria-label={`${item.media_type === 'video' ? 'Odtwórz wideo' : 'Otwórz zdjęcie'}: ${item.title}`}
                      className="group relative block w-full overflow-hidden rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 text-left"
                    >
                      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                        <picture>
                          {item.avif && <source srcSet={item.avif} type="image/avif" />}
                          {item.webp && <source srcSet={item.webp} type="image/webp" />}
                          <img
                            src={item.image_url}
                            alt={item.title}
                            width={800}
                            height={600}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                            style={focalStyle(item.focal_x, item.focal_y)}
                          />
                        </picture>
                        {item.media_type === 'video' && (
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="w-14 h-14 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center ring-2 ring-white/80 group-hover:scale-110 transition-transform">
                              <Play className="w-6 h-6 text-white fill-white ml-0.5" aria-hidden="true" />
                            </div>
                          </div>
                        )}
                      </div>
                      <div
                        className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-foreground/90 via-foreground/60 to-transparent opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity motion-reduce:transition-none"
                        aria-hidden="true"
                      >
                        <p className="text-background text-sm font-semibold leading-tight line-clamp-2">
                          {item.title}
                        </p>
                        {(item.municipality || item.year) && (
                          <p className="text-background/80 text-xs mt-1">
                            {item.municipality}
                            {item.municipality && item.year ? ' • ' : ''}
                            {item.year}
                          </p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>

                {visibleCount < filtered.length && (
                  <div className="flex justify-center mt-8">
                    <Button
                      type="button"
                      variant="outline"
                      size="lg"
                      onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                    >
                      Pokaż więcej (+{Math.min(PAGE_SIZE, filtered.length - visibleCount)})
                    </Button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {current && lightbox !== null && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Podgląd: ${current.title}`}
            className="fixed inset-0 z-[100] bg-foreground/90 flex items-center justify-center p-4"
            onClick={closeLightbox}
          >
            <button
              ref={closeBtnRef}
              className="absolute top-4 right-4 text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-background rounded p-1 z-10"
              onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
              aria-label="Zamknij podgląd"
            >
              <X size={32} aria-hidden="true" />
            </button>
            <button
              ref={prevBtnRef}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-background rounded p-1 z-10"
              onClick={(e) => { e.stopPropagation(); navigate(-1); }}
              aria-label="Poprzednie"
            >
              <ChevronLeft size={40} aria-hidden="true" />
            </button>

            <div
              className="bg-background text-foreground rounded-panel max-w-3xl w-full max-h-[90vh] overflow-y-auto flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {current.media_type === 'video' ? (
                <VideoEmbed item={current} />
              ) : (
                <img
                  src={current.image_url}
                  alt={current.title}
                  width={1600}
                  height={1200}
                  className="w-full h-auto max-h-[60vh] object-contain bg-foreground/5"
                />
              )}
              <div className="p-5 sm:p-6">
                <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                  {current.municipality && (
                    <Badge variant="secondary" className="bg-accent/20 text-accent border-0">
                      {current.municipality}
                    </Badge>
                  )}
                  {current.year && (
                    <span className="text-xs uppercase tracking-wider opacity-90">{current.year}</span>
                  )}
                </div>
                <h3 className="font-heading font-bold text-2xl mb-3">{current.title}</h3>
                {current.description && (
                  <p className="leading-relaxed mb-4 whitespace-pre-wrap">{current.description}</p>
                )}
                {current.credit && (
                  <p className="text-xs opacity-90 flex items-center gap-1.5">
                    <Camera className="w-3.5 h-3.5" aria-hidden="true" />
                    {current.credit}
                  </p>
                )}
                <p className="text-xs opacity-90 mt-4">
                  {lightbox + 1} z {filtered.length}
                </p>
              </div>
            </div>

            <button
              ref={nextBtnRef}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-background rounded p-1 z-10"
              onClick={(e) => { e.stopPropagation(); navigate(1); }}
              aria-label="Następne"
            >
              <ChevronRight size={40} aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default GallerySection;
